/**
 * Shared calculation logic for Protein Pal.
 *
 * NOT the single source of truth, despite what this header used to claim.
 * Imported by FormulaCompare and FormulaListScreen only. App.tsx (the
 * calculator, and the engine the Calculation Validation audit prints) has its
 * own inline copy and never imports this file; FormulaReport.tsx has a third;
 * protein-pal-headless is a fourth. Where they disagree, App.tsx is the one the
 * lab and the label follow.
 *
 * Change anything here and check whether the same expression exists in App.tsx
 * and FormulaReport.tsx before assuming the fix has landed everywhere.
 * Unifying them is tracked as BUGS.md P2 #9.
 */

/* ── Constants ─────────────────────────────────────────────────────────────── */

// Canada/WHO Reference Pattern (mg/g protein) — 2-5 year old (WHO/FAO/UNU 2007)
export const REFERENCE_PATTERN: Record<string, number> = {
  histidine: 19,
  isoleucine: 28,
  leucine: 66,
  lysine: 58,
  methionine_cysteine: 25,
  phenylalanine_tyrosine: 63,
  threonine: 34,
  tryptophan: 11,
  valine: 35,
};

export const AA_NAMES: Record<string, string> = {
  histidine: "His",
  isoleucine: "Ile",
  leucine: "Leu",
  lysine: "Lys",
  methionine_cysteine: "M+C",
  phenylalanine_tyrosine: "P+T",
  threonine: "Thr",
  tryptophan: "Trp",
  valine: "Val",
};

export const AA_FULL_NAMES: Record<string, string> = {
  histidine: "Histidine",
  isoleucine: "Isoleucine",
  leucine: "Leucine",
  lysine: "Lysine",
  methionine_cysteine: "Met + Cys",
  phenylalanine_tyrosine: "Phe + Tyr",
  threonine: "Threonine",
  tryptophan: "Tryptophan",
  valine: "Valine",
};

// Daily Reference Values (21 CFR 101.9)
export const DRV: Record<string, number> = {
  fat: 78,
  satfat: 20,
  cholesterol: 300,
  sodium: 2300,
  carbs: 275,
  fiber: 28,
  protein: 50,
  vitaminD: 20,
  calcium: 1300,
  iron: 18,
  potassium: 4700,
};

/* ── FDA Rounding Rules (21 CFR 101.9) ─────────────────────────────────── */

export const fdaRound = {
  calories: (val: number): number => {
    if (val < 5) return 0;
    if (val <= 50) return Math.round(val / 5) * 5;
    return Math.round(val / 10) * 10;
  },
  fat: (val: number): number => {
    if (val < 0.5) return 0;
    if (val < 5) return Math.round(val * 2) / 2;
    return Math.round(val);
  },
  satfat: (val: number): number => {
    if (val < 0.5) return 0;
    if (val < 5) return Math.round(val * 2) / 2;
    return Math.round(val);
  },
  transfat: (val: number): number => {
    if (val < 0.5) return 0;
    if (val < 5) return Math.round(val * 2) / 2;
    return Math.round(val);
  },
  cholesterol: (val: number): number | string => {
    if (val < 2) return 0;
    if (val <= 5) return "<5";
    return Math.round(val / 5) * 5;
  },
  sodium: (val: number): number => {
    if (val < 5) return 0;
    if (val <= 140) return Math.round(val / 5) * 5;
    return Math.round(val / 10) * 10;
  },
  carbs: (val: number): number => (val < 0.5 ? 0 : Math.round(val)),
  fiber: (val: number): number => (val < 0.5 ? 0 : Math.round(val)),
  sugar: (val: number): number => (val < 0.5 ? 0 : Math.round(val)),
  protein: (val: number): number => (val < 0.5 ? 0 : Math.round(val)),
  vitaminD: (val: number): number => {
    if (val < 0.1) return 0;
    if (val < 10) return Math.round(val * 10) / 10;
    return Math.round(val);
  },
  calcium: (val: number): number => {
    if (val < 10) return 0;
    if (val <= 100) return Math.round(val / 10) * 10;
    if (val <= 1000) return Math.round(val / 50) * 50;
    return Math.round(val / 100) * 100;
  },
  iron: (val: number): number => {
    if (val < 0.5) return 0;
    if (val < 2) return Math.round(val * 10) / 10;
    return Math.round(val * 2) / 2;
  },
  potassium: (val: number): number => {
    if (val < 5) return 0;
    if (val <= 140) return Math.round(val / 5) * 5;
    return Math.round(val / 10) * 10;
  },
  servingsPerContainer: (val: number) => {
    if (val < 2)
      return { value: Math.round(val), display: String(Math.round(val)), needsAbout: false };
    if (val <= 5) {
      const rounded = Math.round(val * 2) / 2;
      return { value: rounded, display: String(rounded), needsAbout: true };
    }
    const rounded = Math.round(val);
    return { value: rounded, display: String(rounded), needsAbout: true };
  },
  piecesPerServing: (val: number): number => Math.round(val),
};

/* ── Ingredient type (common shape) ──────────────────────────────────────── */

export interface CalcIngredient {
  protein_per_100g: number;
  digestibility: number | null;
  cost_per_kg: number;
  fat: number;
  satfat: number;
  transfat: number;
  cholesterol: number;
  carbs: number;
  fiber: number;
  sugar: number;
  sodium: number;
  moisture: number;
  vitaminD: number;
  calcium: number;
  iron: number;
  potassium: number;
  color: string;
  amino_acids: Record<string, number>;
  // Optional extended fields
  fa_ala?: number;
  fa_la?: number;
  fa_oleic?: number;
  fa_palmitic?: number;
  fa_stearic?: number;
  fiber_insoluble?: number;
  fiber_soluble?: number;
  resistant_starch?: number;
  [key: string]: any;
}

/* ── Core compositing: compute per-100g nutrients from formula ────────── */

export interface CompositeNutrients {
  protein: number;
  fat: number;
  satfat: number;
  transfat: number;
  cholesterol: number;
  carbs: number;
  fiber: number;
  sugar: number;
  sodium: number;
  moisture: number;
  vitaminD: number;
  calcium: number;
  iron: number;
  potassium: number;
  calories: number;
  cost: number;
  // Fiber fractions
  fiberInsoluble: number;
  fiberSoluble: number;
  resistantStarch: number;
  // Fatty acids
  faAla: number;
  faLa: number;
  faOleic: number;
  faPalmitic: number;
  faStearic: number;
}

/**
 * Compute per-100g composite nutrients from a flat formula
 * (ingredient name → pct of total).
 */
export function computeCompositeNutrients(
  totalFormula: Record<string, number>,
  ingredients: Record<string, CalcIngredient>,
): CompositeNutrients {
  let protein = 0, fat = 0, satfat = 0, transfat = 0, cholesterol = 0;
  let carbs = 0, fiber = 0, sugar = 0, sodium = 0, moisture = 0;
  let vitaminD = 0, calcium = 0, iron = 0, potassium = 0, cost = 0;
  let fiberInsoluble = 0, fiberSoluble = 0, resistantStarch = 0;
  let faAla = 0, faLa = 0, faOleic = 0, faPalmitic = 0, faStearic = 0;

  Object.entries(totalFormula).forEach(([name, pct]) => {
    const ing = ingredients[name];
    if (!ing || pct <= 0) return;
    const f = pct / 100;
    protein += f * ing.protein_per_100g;
    fat += f * ing.fat;
    satfat += f * (ing.satfat || 0);
    transfat += f * (ing.transfat || 0);
    cholesterol += f * (ing.cholesterol || 0);
    carbs += f * ing.carbs;
    fiber += f * ing.fiber;
    sugar += f * ing.sugar;
    sodium += f * ing.sodium;
    moisture += f * ing.moisture;
    vitaminD += f * (ing.vitaminD || 0);
    calcium += f * (ing.calcium || 0);
    iron += f * (ing.iron || 0);
    potassium += f * (ing.potassium || 0);
    cost += (f * (ing.cost_per_kg || 0)) / 10;
    fiberInsoluble += f * (ing.fiber_insoluble || 0);
    fiberSoluble += f * (ing.fiber_soluble || 0);
    resistantStarch += f * (ing.resistant_starch || 0);
    faAla += f * (ing.fa_ala || 0);
    faLa += f * (ing.fa_la || 0);
    faOleic += f * (ing.fa_oleic || 0);
    faPalmitic += f * (ing.fa_palmitic || 0);
    faStearic += f * (ing.fa_stearic || 0);
  });

  const calories = protein * 4 + carbs * 4 + fat * 9;

  return {
    protein, fat, satfat, transfat, cholesterol,
    carbs, fiber, sugar, sodium, moisture,
    vitaminD, calcium, iron, potassium, calories, cost,
    fiberInsoluble, fiberSoluble, resistantStarch,
    faAla, faLa, faOleic, faPalmitic, faStearic,
  };
}

/* ── Amino acid compositing (protein-weighted) ───────────────────────── */

export interface AAResult {
  blendedAA: Record<string, number>;
  aaScores: Record<string, number>;
  limitingAA: string | null;
  limitingScore: number;
  aminoAcidScore: number;
}

/**
 * Compute protein-weighted blended amino acid profile and scores.
 */
export function computeAminoAcids(
  totalFormula: Record<string, number>,
  ingredients: Record<string, CalcIngredient>,
): AAResult {
  // Build protein contributions
  let totalProtein = 0;
  const proteinContribs: { protein: number; amino_acids: Record<string, number> }[] = [];

  Object.entries(totalFormula).forEach(([name, pct]) => {
    const ing = ingredients[name];
    if (!ing || pct <= 0) return;
    const proteinContrib = (pct / 100) * ing.protein_per_100g;
    if (proteinContrib > 0.01) {
      totalProtein += proteinContrib;
      proteinContribs.push({ protein: proteinContrib, amino_acids: ing.amino_acids });
    }
  });

  const blendedAA: Record<string, number> = {};
  Object.keys(REFERENCE_PATTERN).forEach((aa) => {
    blendedAA[aa] = 0;
    proteinContribs.forEach((data) => {
      if (totalProtein > 0) {
        blendedAA[aa] += (data.amino_acids[aa] || 0) * (data.protein / totalProtein);
      }
    });
  });

  const aaScores: Record<string, number> = {};
  let limitingAA: string | null = null;
  let limitingScore = Infinity;
  Object.keys(REFERENCE_PATTERN).forEach((aa) => {
    const score = blendedAA[aa] / REFERENCE_PATTERN[aa];
    aaScores[aa] = score;
    if (score < limitingScore) {
      limitingScore = score;
      limitingAA = aa;
    }
  });

  const aminoAcidScore = Math.min(limitingScore, 1);
  return { blendedAA, aaScores, limitingAA, limitingScore, aminoAcidScore };
}

/* ── Weighted digestibility ───────────────────────────────────────────── */

export function computeWeightedDigestibility(
  totalFormula: Record<string, number>,
  ingredients: Record<string, CalcIngredient>,
): number {
  let totalProtein = 0;
  let weightedSum = 0;
  Object.entries(totalFormula).forEach(([name, pct]) => {
    const ing = ingredients[name];
    if (!ing || pct <= 0) return;
    const proteinContrib = (pct / 100) * ing.protein_per_100g;
    if (proteinContrib > 0.01) {
      totalProtein += proteinContrib;
      weightedSum += (ing.digestibility ?? 0) * proteinContrib;
    }
  });
  return totalProtein > 0 ? weightedSum / totalProtein : 0;
}

/* ── PDCAAS ───────────────────────────────────────────────────────────── */

export interface PdcaasResult {
  weightedDigestibility: number;
  aminoAcidScore: number;
  pdcaas: number;
  truncatedPdcaas: number;
  limitingAA: string | null;
  limitingScore: number;
  blendedAA: Record<string, number>;
  aaScores: Record<string, number>;
  correctedProteinPer100g: number;
}

export function computePdcaas(
  totalFormula: Record<string, number>,
  ingredients: Record<string, CalcIngredient>,
  proteinPer100g: number,
): PdcaasResult {
  const wd = computeWeightedDigestibility(totalFormula, ingredients);
  const aa = computeAminoAcids(totalFormula, ingredients);
  const pdcaas = aa.aminoAcidScore * wd;
  const truncatedPdcaas = Math.min(pdcaas, 1);
  return {
    weightedDigestibility: wd,
    aminoAcidScore: aa.aminoAcidScore,
    pdcaas,
    truncatedPdcaas,
    limitingAA: aa.limitingAA,
    limitingScore: aa.limitingScore,
    blendedAA: aa.blendedAA,
    aaScores: aa.aaScores,
    correctedProteinPer100g: proteinPer100g * truncatedPdcaas,
  };
}

/* ── Build flat totalFormula from dough + filling + ratio ─────────────── */

export function buildTotalFormula(
  doughFormula: Record<string, number>,
  fillingFormula: Record<string, number>,
  doughRatioPct: number,
): Record<string, number> {
  const total: Record<string, number> = {};
  Object.entries(doughFormula).forEach(([name, pct]) => {
    const contrib = (pct / 100) * doughRatioPct;
    total[name] = (total[name] || 0) + contrib;
  });
  Object.entries(fillingFormula).forEach(([name, pct]) => {
    const contrib = (pct / 100) * (100 - doughRatioPct);
    total[name] = (total[name] || 0) + contrib;
  });
  return total;
}

/* ── Post-process moisture adjustment ──────────────────────────────────── */

/**
 * Apply post-process moisture adjustment to per-100g nutrients.
 * Returns finished nutrients per 100g of finished product.
 *
 * moistureInitial: as-mixed moisture (g/100g)
 * moistureTarget: final moisture target (g/100g)
 * concentrationFactor = (100 - moistureTarget) / (100 - moistureInitial)
 */
export function applyMoistureConcentration(
  nutrients: CompositeNutrients,
  moistureTarget: number,
): CompositeNutrients {
  const moistureInitial = nutrients.moisture;
  if (Math.abs(moistureTarget - moistureInitial) < 0.01) {
    return { ...nutrients, moisture: moistureTarget };
  }
  const cf = (100 - moistureInitial) > 0.01
    ? (100 - moistureTarget) / (100 - moistureInitial)
    : 1;

  return {
    protein: nutrients.protein * cf,
    fat: nutrients.fat * cf,
    satfat: nutrients.satfat * cf,
    transfat: nutrients.transfat * cf,
    cholesterol: nutrients.cholesterol * cf,
    carbs: nutrients.carbs * cf,
    fiber: nutrients.fiber * cf,
    sugar: nutrients.sugar * cf,
    sodium: nutrients.sodium * cf,
    moisture: moistureTarget,
    vitaminD: nutrients.vitaminD * cf,
    calcium: nutrients.calcium * cf,
    iron: nutrients.iron * cf,
    potassium: nutrients.potassium * cf,
    calories: nutrients.protein * cf * 4 + nutrients.carbs * cf * 4 + nutrients.fat * cf * 9,
    cost: nutrients.cost * cf,
    fiberInsoluble: nutrients.fiberInsoluble * cf,
    fiberSoluble: nutrients.fiberSoluble * cf,
    resistantStarch: nutrients.resistantStarch * cf,
    faAla: nutrients.faAla * cf,
    faLa: nutrients.faLa * cf,
    faOleic: nutrients.faOleic * cf,
    faPalmitic: nutrients.faPalmitic * cf,
    faStearic: nutrients.faStearic * cf,
  };
}

/**
 * Full finished-good calculation for a formula with post-process settings.
 * Returns per-100g finished nutrients, amino acids, PDCAAS.
 */
export function computeFinishedGood(
  doughFormula: Record<string, number>,
  fillingFormula: Record<string, number>,
  doughRatioPct: number,
  ratioMode: string,
  finalMoistureMode: string | null,
  finalMoistureWhole: number | null,
  finalMoistureDough: number | null,
  finalMoistureFilling: number | null,
  ingredients: Record<string, CalcIngredient>,
): {
  nutrients: CompositeNutrients;
  pdcaas: PdcaasResult;
  effectiveDoughRatio: number;
  effectiveFillingRatio: number;
  isPostProcessed: boolean;
} {
  // No post-process: simple composite
  if (!finalMoistureMode) {
    const totalFormula = buildTotalFormula(doughFormula, fillingFormula, doughRatioPct);
    const nutrients = computeCompositeNutrients(totalFormula, ingredients);
    const pdcaas = computePdcaas(totalFormula, ingredients, nutrients.protein);
    return {
      nutrients,
      pdcaas,
      effectiveDoughRatio: doughRatioPct,
      effectiveFillingRatio: 100 - doughRatioPct,
      isPostProcessed: false,
    };
  }

  // Post-process: composite mode
  if (finalMoistureMode === "composite") {
    const totalFormula = buildTotalFormula(doughFormula, fillingFormula, doughRatioPct);
    const asMixed = computeCompositeNutrients(totalFormula, ingredients);
    const target = finalMoistureWhole ?? asMixed.moisture;
    const finished = applyMoistureConcentration(asMixed, target);
    const pdcaas = computePdcaas(totalFormula, ingredients, finished.protein);
    return {
      nutrients: finished,
      pdcaas,
      effectiveDoughRatio: doughRatioPct,
      effectiveFillingRatio: 100 - doughRatioPct,
      isPostProcessed: true,
    };
  }

  // Post-process: components mode
  // Compute per-component nutrients separately
  const doughTotal: Record<string, number> = {};
  Object.entries(doughFormula).forEach(([n, p]) => { doughTotal[n] = p; });
  const fillingTotal: Record<string, number> = {};
  Object.entries(fillingFormula).forEach(([n, p]) => { fillingTotal[n] = p; });

  const doughNutrients = computeCompositeNutrients(doughTotal, ingredients);
  const fillingNutrients = computeCompositeNutrients(fillingTotal, ingredients);

  // Apply moisture adjustment per component (null = no change)
  const doughTarget = finalMoistureDough ?? doughNutrients.moisture;
  const fillingTarget = finalMoistureFilling ?? fillingNutrients.moisture;

  const doughFinished = applyMoistureConcentration(doughNutrients, doughTarget);
  const fillingFinished = applyMoistureConcentration(fillingNutrients, fillingTarget);

  // Determine effective ratio
  let effDoughRatio: number;
  let effFillingRatio: number;

  if (ratioMode === "finished") {
    // dough_ratio_pct IS the finished ratio — combine directly
    effDoughRatio = doughRatioPct;
    effFillingRatio = 100 - doughRatioPct;
  } else {
    // ratio_mode = as_mixed: derive finished ratio from weight changes
    const doughCf = (100 - doughNutrients.moisture) > 0.01
      ? (100 - doughTarget) / (100 - doughNutrients.moisture) : 1;
    const fillingCf = (100 - fillingNutrients.moisture) > 0.01
      ? (100 - fillingTarget) / (100 - fillingNutrients.moisture) : 1;
    const doughWeight = doughRatioPct / doughCf;
    const fillingWeight = (100 - doughRatioPct) / fillingCf;
    const totalWeight = doughWeight + fillingWeight;
    effDoughRatio = totalWeight > 0 ? (doughWeight / totalWeight) * 100 : doughRatioPct;
    effFillingRatio = 100 - effDoughRatio;
  }

  // Combine finished components at effective ratio
  const dr = effDoughRatio / 100;
  const fr = effFillingRatio / 100;
  const combined: CompositeNutrients = {
    protein: doughFinished.protein * dr + fillingFinished.protein * fr,
    fat: doughFinished.fat * dr + fillingFinished.fat * fr,
    satfat: doughFinished.satfat * dr + fillingFinished.satfat * fr,
    transfat: doughFinished.transfat * dr + fillingFinished.transfat * fr,
    cholesterol: doughFinished.cholesterol * dr + fillingFinished.cholesterol * fr,
    carbs: doughFinished.carbs * dr + fillingFinished.carbs * fr,
    fiber: doughFinished.fiber * dr + fillingFinished.fiber * fr,
    sugar: doughFinished.sugar * dr + fillingFinished.sugar * fr,
    sodium: doughFinished.sodium * dr + fillingFinished.sodium * fr,
    moisture: doughFinished.moisture * dr + fillingFinished.moisture * fr,
    vitaminD: doughFinished.vitaminD * dr + fillingFinished.vitaminD * fr,
    calcium: doughFinished.calcium * dr + fillingFinished.calcium * fr,
    iron: doughFinished.iron * dr + fillingFinished.iron * fr,
    potassium: doughFinished.potassium * dr + fillingFinished.potassium * fr,
    calories: 0,
    cost: doughFinished.cost * dr + fillingFinished.cost * fr,
    fiberInsoluble: doughFinished.fiberInsoluble * dr + fillingFinished.fiberInsoluble * fr,
    fiberSoluble: doughFinished.fiberSoluble * dr + fillingFinished.fiberSoluble * fr,
    resistantStarch: doughFinished.resistantStarch * dr + fillingFinished.resistantStarch * fr,
    faAla: doughFinished.faAla * dr + fillingFinished.faAla * fr,
    faLa: doughFinished.faLa * dr + fillingFinished.faLa * fr,
    faOleic: doughFinished.faOleic * dr + fillingFinished.faOleic * fr,
    faPalmitic: doughFinished.faPalmitic * dr + fillingFinished.faPalmitic * fr,
    faStearic: doughFinished.faStearic * dr + fillingFinished.faStearic * fr,
  };
  combined.calories = combined.protein * 4 + combined.carbs * 4 + combined.fat * 9;

  // AA/PDCAAS: use the as-mixed formula for protein-weighting (AA in mg/g protein don't change)
  const totalFormula = buildTotalFormula(doughFormula, fillingFormula,
    ratioMode === "finished" ? doughRatioPct : doughRatioPct);
  const pdcaas = computePdcaas(totalFormula, ingredients, combined.protein);

  return {
    nutrients: combined,
    pdcaas,
    effectiveDoughRatio: effDoughRatio,
    effectiveFillingRatio: effFillingRatio,
    isPostProcessed: true,
  };
}

/* ── Claim determination ──────────────────────────────────────────────── */

export function getProteinClaim(percentDV: number): { claim: string; color: string } {
  if (percentDV >= 20) return { claim: "Excellent Source", color: "#22c55e" };
  if (percentDV >= 10) return { claim: "Good Source", color: "#f59e0b" };
  return { claim: "No Claim", color: "#ef4444" };
}

export function getFiberClaim(fiberPerServing: number): { claim: string; color: string } {
  if (fiberPerServing >= 5) return { claim: "Excellent Source", color: "#22c55e" };
  if (fiberPerServing >= 2.5) return { claim: "Good Source", color: "#f59e0b" };
  return { claim: "No Claim", color: "#ef4444" };
}

/* ── Per-serving scaling ──────────────────────────────────────────────── */

export function scaleToServing(per100g: number, servingSizeG: number): number {
  return (per100g * servingSizeG) / 100;
}
